/**
 * Document Permissions Manager
 * Admin UI for granting/revoking document access per user.
 * By default, no user has document access until explicitly granted.
 */

import { useState, useEffect } from 'react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, Search, Shield, Info } from 'lucide-react';
import {
  DOC_PERMISSIONS,
  hasDocPermission,
  toggleDocPermission,
  useDocumentUsers,
  useUpdateDocPermissions,
  type DocPermissionKey,
  type UserWithPermissions,
} from '../../hooks/useDocumentPermissions';

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function UserRow({ user }: { user: UserWithPermissions }) {
  const update = useUpdateDocPermissions();

  const handleToggle = (key: DocPermissionKey, enabled: boolean) => {
    const newPerms = toggleDocPermission(user.permission_strings, key, enabled);
    update.mutate({ userId: user._id, permissionStrings: newPerms, permissionKey: key });
  };

  const isUpdating = (key: string) =>
    update.isPending && update.variables?.userId === user._id && update.variables?.permissionKey === key;

  const displayName = user.firstname || user.lastname
    ? `${user.firstname} ${user.lastname}`.trim()
    : user.email;

  return (
    <TableRow>
      <TableCell className="font-medium min-w-[200px]">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900 dark:text-foreground">{displayName}</span>
          <span className="text-xs text-gray-500 dark:text-muted-foreground">{user.email}</span>
        </div>
      </TableCell>
      {DOC_PERMISSIONS.map((perm) => (
        <TableCell key={perm.key} className="text-center">
          {isUpdating(perm.key) ? (
            <Loader2 className="h-4 w-4 animate-spin mx-auto text-gray-400" />
          ) : (
            <Switch
              checked={hasDocPermission(user.permission_strings, perm.key)}
              onCheckedChange={(checked) => handleToggle(perm.key, checked)}
            />
          )}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function DocumentPermissionsManager() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { data: users, isLoading, error } = useDocumentUsers(debouncedSearch);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
            <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <CardTitle>Document Permissions</CardTitle>
            <CardDescription>
              Control who can access, upload, edit, and delete documents. By default, no user has access.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Users table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading users...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-sm text-red-500">
            Failed to load users: {(error as Error).message}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="min-w-[200px]">User</TableHead>
                  {DOC_PERMISSIONS.map((perm) => (
                    <TableHead key={perm.key} className="text-center w-24">
                      {perm.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users && users.length > 0 ? (
                  users.map((user) => <UserRow key={user._id} user={user} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={DOC_PERMISSIONS.length + 1} className="text-center py-12 text-gray-500">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Legend */}
        <div className="bg-gray-50 dark:bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Permission descriptions</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {DOC_PERMISSIONS.map((perm) => (
              <div key={perm.key} className="text-xs">
                <span className="font-medium text-gray-700 dark:text-foreground">{perm.label}</span>
                <span className="text-gray-500 dark:text-muted-foreground"> — {perm.description}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
